// SPECIFIC for a AB TEST
// This is ONE OFF reusable CAROUSEL component that will except data to create ARTWORKS components
// Parent Container will fetch all ARTWORKS and data adn pass it in
// Since all CAROUSELS should be the same there will be limited ability to customize
// If you want to REUSE you may want to DUPLICATE to create custom Logic
import React, { useState, useEffect, useRef } from 'react'
import SALink from 'components/SALink'
import ABTestArtwork from 'components/Carousel/Artworks/ABTestArtwork'
import useCarousel from 'hooks/carouselTimer' // Basic Position Logic

import { NextIcon, PrevIcon, RewindIcon } from './styles'
const ITEM_WIDTH = 176
const MARGIN = 45
type Props = {
  artworks: Array<Record<string, any>>
  clickArtwork: (...args: Array<any>) => any
  dataApi: string
  dataList: string
  dataListId: string
  dataListName: string
  seeMore: {
    link: string
    title: string
  }
  hideSeeMore?: boolean
  parentDataSection?: string
  algoliaSearchIndex: string
  algoliaUserToken: string
  algoliaQueryID: string
  userID: number
}
type SectionProps = {
  containerRef: Record<string, any>
  contentRef: Record<string, any>
}

const getElementDimensions = (ref: Record<string, any>): Record<string, any> => {
  return ref?.current?.getBoundingClientRect?.()?.toJSON?.() || {}
}

const getChildrenLength = (ref: Record<string, any>): number => {
  if (ref.current) {
    const { childElementCount } = ref.current // children => HTMLCollection (OBJECT)

    return childElementCount
  }

  return 0
}

// Resize - Find Children Width / ItemIndex
const getSectionsArray = ({ containerRef, contentRef }: SectionProps): Record<string, any> => {
  const container = getElementDimensions(containerRef)
  const listItems = getChildrenLength(contentRef)
  // We want to keep how many items fit in view, before we have to create a new sections
  const limit = listItems - 1 // how many items inside VIEW

  const sections = [0] // keep track of how many sections are available

  let count = 0 // how many items have we done ( incremented, put in sections )

  let totalSize = 0

  while (count <= limit) {
    let size = 0

    while (size < container.width) {
      // We want to make sure we don't fall into a INFINITE LOOP
      // Make sure we have already incremented added one item to SIZE
      // Problem: if the item is bigger then container, we would never increment
      if (size && size + ITEM_WIDTH > container.width) {
        // We have met Width of Container, Escape early and start new section
        break
      }

      size += ITEM_WIDTH + MARGIN
      count++

      // Make sure we END the last section
      if (count > limit) {
        break
      }
    }

    if (count > limit) {
      // We need to overwrite the last value
      // We want to show last item FLUSH against the right side
      totalSize -= container.width + MARGIN - size
      sections[sections.length - 1] = totalSize
    } else {
      totalSize += size
      sections.push(totalSize)
    }
  }

  return {
    sections,
    containerWidth: container.width,
  }
}

const Carousel = (props: Props): React.ReactElement<React.ComponentProps<any>, any> => {
  const {
    artworks,
    dataApi,
    dataList,
    clickArtwork,
    dataListId,
    dataListName,
    seeMore: { link: seeMoreLink, title: seeMoreTitle },
    hideSeeMore,
    parentDataSection,
    algoliaSearchIndex,
    algoliaUserToken,
    userID,
    algoliaQueryID,
  } = props
  const containerRef: Record<string, any> = useRef()
  const contentRef: Record<string, any> = useRef()
  const scrollRef: Record<string, any> = useRef()
  const [sections, handleSections] = useState([])
  const { nextPosition, position, prevPosition, setItems, setPosition } = useCarousel({
    items: 0,
    enableTimer: false,
    timerDuration: 3000,
  })
  useEffect(() => {
    const { sections: sectionArray } = getSectionsArray({
      containerRef,
      contentRef,
    })

    // Reset Mobile Scroller
    if (scrollRef.current) {
      scrollRef.current.scrollLeft = 0
    }

    handleSections(sectionArray)
    setItems(sectionArray.length)
    setPosition(0)
  }, [artworks])

  const handlePrevPosition = (ev: React.SyntheticEvent<HTMLButtonElement>) => {
    ev.preventDefault()
    prevPosition()
  }

  const handleNextPosition = (ev: React.SyntheticEvent<HTMLButtonElement>) => {
    ev.preventDefault()
    nextPosition()
  }

  const handleSetPosition = (ev: React.SyntheticEvent<HTMLButtonElement>) => {
    ev.preventDefault()
    setPosition(0)
  }

  const translateX = sections[position] || 0
  return (
    <div ref={containerRef} data-type='carousel-wrapper'>
      <div data-type='controls'>
        {position > 0 && (
          <button
            data-action='previous'
            type='button'
            onClick={handlePrevPosition}
            onKeyPress={handlePrevPosition}
          >
            <PrevIcon />
          </button>
        )}

        {position < sections.length - 1 && (
          <button
            data-action='next'
            type='button'
            onClick={handleNextPosition}
            onKeyPress={handleNextPosition}
          >
            <NextIcon />
          </button>
        )}

        {sections.length && position === sections.length - 1 && (
          <button
            data-action='restart'
            type='button'
            onClick={handleSetPosition}
            onKeyPress={handleSetPosition}
          >
            <RewindIcon />
            <span>Restart</span>
          </button>
        )}
      </div>

      <div ref={scrollRef} data-type='carousel'>
        <ul
          ref={contentRef}
          style={{
            transform: `translateX(-${translateX}px)`,
          }}
          parent-data-section={parentDataSection}
        >
          <>
            {/* Filter Out Current Artwork from Payload */}
            {artworks.map((artwork, key) => (
              <li key={`${dataList || 'carousel'}-${artwork.sku || key}`}>
                <ABTestArtwork
                  {...artwork}
                  clickArtwork={clickArtwork}
                  dataApi={dataApi}
                  dataPosition={key + 1}
                  dataList={dataList}
                  dataListId={dataListId}
                  dataListName={dataListName}
                  data
                  algoliaSearchIndex={algoliaSearchIndex}
                  algoliaUserToken={algoliaUserToken}
                  algoliaQueryID={algoliaQueryID}
                  userID={userID}
                />
              </li>
            ))}

            {!hideSeeMore && (
              <li>
                <SALink data-type='see-more' href={seeMoreLink} title={seeMoreTitle}>
                  <p>See More Art</p>
                </SALink>
              </li>
            )}
          </>
        </ul>
      </div>
    </div>
  )
}

export default Carousel
